import axios, { CancelTokenSource } from 'axios';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

import { IApplicationState } from '../../store/applicationState';
import { fetchGroup, fetchGroupMembers, fetchGroupMessages } from '../../store/group/groupActions';
import { IGroup, IGroupMessage, IGroupMember } from '../../store/group/groupState';

import ActivityList from '../../components/shared/activitylist/ActivityList';
import Message, { DisplayMode } from '../../components/shared/message/Message';
import Spinner, { SpinnerSize } from '../../components/shared/spinner/Spinner';

import * as helper from '../../genericExtensions';

export interface IMessagesRouterParams {
    groupId: string;
}

interface IState {
    cancelToken: CancelTokenSource;
}

interface IStateProps {
    group: IGroup;
    groupMembers: IGroupMember[];
    groupMessages: IGroupMessage[];
    viewerIsDi: boolean;
}

interface IDispatchProps {
    fetchGroup: typeof fetchGroup;
    fetchGroupMembers: typeof fetchGroupMembers;
    fetchGroupMessages: typeof fetchGroupMessages;
}

type Props = IStateProps & IDispatchProps & RouteComponentProps<IMessagesRouterParams>;

class MessagesIndex extends React.Component<Props, IState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            cancelToken: axios.CancelToken.source()
        }
    }

    public async componentDidMount() {
        if (!this.props.group || !this.props.group.id) {
            this.props.fetchGroup(this.props.match.params.groupId, this.state.cancelToken);
        } else {
            this.fetchData();
        }
    }

    public async componentDidUpdate(prevProps: Props) {
        if (prevProps.group !== this.props.group) {
            if (!this.props.group) {
                this.props.fetchGroup(this.props.match.params.groupId, this.state.cancelToken);
            } else if (!prevProps.group || prevProps.group.id !== this.props.group.id) {
                await this.fetchData();
            }
        }
    }

    public componentWillUnmount() {
        this.state.cancelToken.cancel();
    }

    public render() {
        return <React.Fragment>
            <div className="section">
                <div className="deck">
                    <div className="w--md banner-title">
                        <h1>Beskeder og dokumenter</h1>
                    </div>
                </div>
                <div className="deck">
                    <div className="w--md">
                        {this.props.groupMessages ?
                            <ActivityList hasArrow={true}>
                                {this.props.groupMessages.map((m, index: number) =>
                                    <Message message={m} displayMode={DisplayMode.Snippet} group={this.props.group} groupMembers={this.props.groupMembers} index={index} key={m.messageId} isDi={this.props.viewerIsDi} onDeleted={this.loadMessages} />)
                                }
                            </ActivityList>
                            :
                            <Spinner size={SpinnerSize.Large} />
                        }
                    </div>
                </div>
            </div>
        </React.Fragment>;
    }

    private async fetchData() {
        if (this.props.group && this.props.group.id) {
            const group = this.props.fetchGroup(this.props.match.params.groupId);
            const memberTask = this.props.fetchGroupMembers(this.props.group.id, this.state.cancelToken);
            const messagesTask = this.props.fetchGroupMessages(this.props.group.id, this.state.cancelToken);

            await Promise.all([group, memberTask, messagesTask]);
        }
    }

    private loadMessages = async () => {
        await this.props.fetchGroupMessages(this.props.group.id, this.state.cancelToken);
    }
}

const mapStateToProps = (state: IApplicationState, ownProps: RouteComponentProps<IMessagesRouterParams>): IStateProps => {
    const urlGroupId = ownProps.match.params.groupId;
    const potentialGroupIds = state.groupData.groups.groups.allIds.filter(id => {
        const g = state.groupData.groups.groups.byId[id];

        return g.alias === urlGroupId || g.id === urlGroupId;
    });
    const groupId = (potentialGroupIds.length && potentialGroupIds[0]) || urlGroupId;
    const group = state.groupData.groups.groups.byId[groupId];
    const groupMembers = state.groupData.members.byId[groupId] && state.groupData.members.byId[groupId].members && helper.all(state.groupData.members.byId[groupId].members);
    const groupMessages = state.groupData.messages.byId[groupId];
    const viewerIsDi = state.oidc.user.profile.role === 'DI';

    return {
        group,
        groupMembers,
        groupMessages,
        viewerIsDi
    };
}

const mapDispatchToProps = (dispatch: any): IDispatchProps => ({
    fetchGroup: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroup(groupId, cancelToken)),
    fetchGroupMembers: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroupMembers(groupId, cancelToken)),
    fetchGroupMessages: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroupMessages(groupId, cancelToken))
});

const container = withRouter(connect(mapStateToProps, mapDispatchToProps)(MessagesIndex));

export default container;